import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Device, User } from '../types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartSimple, faIndustry, faMemory, faMicrochip, faPaperPlane, faPen, faPowerOff, faRocket, faSave, faTerminal, faTimes, faTrash, faUser, faUserMinus, faUserPlus } from '@fortawesome/free-solid-svg-icons';
import { API_BASE_URL } from '../config/api';
import {
  getLoggingModeErrorMessage,
  LOGGING_MODE_OPTIONS,
  updateDeviceLoggingMode,
} from '../utils/loggingMode';
import AssignUserModal from './AssignUserModal';
import HardwareProfileModal from './HardwareProfileModal';
import OtaModeSelect from './OtaModeSelect';
import { fieldsForOtaMode, resolveOtaMode } from '../utils/otaMode';
import {
  PCB_TYPE_OPTIONS,
  MODEL_TYPE_OPTIONS,
} from './flasher/hardwarePartitionOptions';

const MIN_HARDWARE_PROFILE_FIRMWARE = '3.2.8';
const HARDWARE_PROFILE_TOOLTIP =
  'This feature is only available on devices running at least v3.2.8';

// Minimum firmware version that can act on MQTT commands pushed from the server
// (kept in sync with DeviceRow's MIN_COMMAND_FIRMWARE).
const MIN_COMMAND_FIRMWARE = '2.7.0';
const COMMAND_UNSUPPORTED_TOOLTIP = `Requires firmware v${MIN_COMMAND_FIRMWARE}+ to receive commands over MQTT`;

/** Compare dotted firmware versions (leading "v" ignored). Returns 1 / 0 / -1. */
function compareFirmwareVersions(a: string | undefined, b: string | undefined): number {
  const normalize = (value: string | undefined) =>
    String(value || '')
      .trim()
      .replace(/^v/i, '')
      .split('.')
      .map((part) => Number.parseInt(part, 10) || 0);

  const left = normalize(a);
  const right = normalize(b);
  const len = Math.max(left.length, right.length);
  for (let i = 0; i < len; i += 1) {
    const l = left[i] || 0;
    const r = right[i] || 0;
    if (l > r) return 1;
    if (l < r) return -1;
  }
  return 0;
}

function labelForOption(options: { value: string; label: string }[], value: string | undefined) {
  if (value === undefined || value === null || value === '') return value;
  return options.find((option) => option.value === value)?.label || value;
}

interface EditDeviceProps {
  device: Device;
  onClose: () => void;
  onSave: (updatedDevice: Device) => void;
  onChange?: (updatedDevice: Device) => void;
  onDelete?: (deletedDevice: Device) => void;
  canEditDeploymentGroup?: boolean;
  variant?: 'modal' | 'page';
}

const getAssignedUser = (device: Device): Device['user'] => {
  if (typeof device.user === 'object' && device.user?._id) {
    return device.user;
  }
  if (typeof device.user === 'string' && device.user) {
    return { _id: device.user, firstName: '', lastName: '' };
  }
  return null;
};

const normalizeDeviceFromResponse = (device: Device, responseDevice: Device): Device => ({
  ...device,
  ...responseDevice,
  user: getAssignedUser(responseDevice),
});

const formatDateTime = (value: Date | string | null | undefined) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const formatBoolean = (value: boolean | null | undefined) => {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return '—';
};

const formatValue = (value: unknown) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return formatBoolean(value);
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
};

const FieldTile: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="border-bottom pb-2">
    <div className="text-muted text-uppercase" style={{ fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.03em' }}>
      {label}
    </div>
    {children}
  </div>
);

const SectionTitle: React.FC<{ icon: typeof faUser; children: React.ReactNode }> = ({ icon, children }) => (
  <h6 className="fw-bold mb-2">
    <FontAwesomeIcon icon={icon} className="me-2 text-muted" />
    {children}
  </h6>
);

const ReadOnlyField: React.FC<{ label: string; value: unknown; code?: boolean; italic?: boolean }> = ({
  label,
  value,
  code = false,
  italic = false,
}) => {
  const displayValue = formatValue(value);

  return (
    <FieldTile label={label}>
      <div className={`small text-break ${code ? 'font-monospace' : ''} ${italic ? 'fst-italic' : ''}`} style={{ whiteSpace: code ? 'pre-wrap' : 'normal' }}>
        {displayValue}
      </div>
    </FieldTile>
  );
};

const typeControlClasses: Record<Device['deployment']['type'], string> = {
  prod: 'border-success text-success',
  beta: 'border-warning text-warning',
  dev: 'border-info text-info',
  deco: 'border-secondary text-secondary',
};

const EditDevice: React.FC<EditDeviceProps> = ({
  device,
  onClose,
  onSave,
  onChange,
  onDelete,
  canEditDeploymentGroup = true,
  variant = 'modal',
}) => {
  const [type, setType] = useState(device.deployment.type);
  const [group, setGroup] = useState(device.deployment.group);
  const [otaMode, setOtaMode] = useState(resolveOtaMode(device.deployment));
  const [loggingMode, setLoggingMode] = useState(device.loggingMode ?? 0);
  const [updatingLoggingMode, setUpdatingLoggingMode] = useState(false);
  const [loggingModeError, setLoggingModeError] = useState<string | null>(null);
  const [assignedUser, setAssignedUser] = useState<Device['user']>(getAssignedUser(device));
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showHardwareProfileModal, setShowHardwareProfileModal] = useState(false);
  const [userActionError, setUserActionError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [rebooting, setRebooting] = useState(false);
  const [rebootFeedback, setRebootFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [commandText, setCommandText] = useState('');
  const [sendingCommand, setSendingCommand] = useState(false);
  const [commandFeedback, setCommandFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const isPage = variant === 'page';
  const canEditHardwareProfile =
    compareFirmwareVersions(device.firmware, MIN_HARDWARE_PROFILE_FIRMWARE) >= 0;
  const supportsRemoteCommands =
    compareFirmwareVersions(device.firmware, MIN_COMMAND_FIRMWARE) >= 0;

  const alphabet = Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i));

  useEffect(() => {
    setType(device.deployment.type);
    setGroup(device.deployment.group);
    setOtaMode(resolveOtaMode(device.deployment));
    setLoggingMode(device.loggingMode ?? 0);
    setLoggingModeError(null);
    setAssignedUser(getAssignedUser(device));
  }, [device]);

  const getUserDisplayName = (user: NonNullable<Device['user']>) => {
    const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
    return name || user._id;
  };

  const renderMqttOnlineIndicator = () => {
    const isOnline = device.mqttOnline === true;
    return (
      <div className="d-flex align-items-center gap-2 small">
        <span
          className={`rounded-circle ${isOnline ? 'bg-success' : 'bg-danger'}`}
          style={{ width: '0.65rem', height: '0.65rem', display: 'inline-block' }}
          aria-label={isOnline ? 'Online' : 'Offline'}
          title={isOnline ? 'Online' : 'Offline'}
        />
        <span className="text-muted">{isOnline ? 'Online' : 'Offline'}</span>
      </div>
    );
  };

  const notifyChange = (updatedDevice: Device) => {
    onChange?.(updatedDevice);
  };

  const handleLoggingModeChange = async (mode: number) => {
    const previousMode = loggingMode;
    setLoggingMode(mode);
    setLoggingModeError(null);
    setUpdatingLoggingMode(true);

    try {
      const { device: updatedDevice } = await updateDeviceLoggingMode(
        API_BASE_URL,
        device.serial,
        mode,
      );
      notifyChange({ ...device, ...updatedDevice, loggingMode: mode });
    } catch (error) {
      console.error('Failed to update device logging mode:', error);
      setLoggingMode(previousMode);
      setLoggingModeError(getLoggingModeErrorMessage(error));
    } finally {
      setUpdatingLoggingMode(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const deployment: Device['deployment'] = {
        ...device.deployment,
        type,
        ...fieldsForOtaMode(otaMode),
        ...(canEditDeploymentGroup ? { group } : {}),
      };

      const updatePayload = {
        deployment,
        loggingMode,
      };

      const response = await axios.patch(
        `${API_BASE_URL}/devices/${device.serial}`,
        updatePayload,
        { withCredentials: true }
      );

      const updatedDevice = normalizeDeviceFromResponse(device, response.data.device);
      onSave(updatedDevice);
      onClose();
    } catch (error) {
      console.error('Failed to save device:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleReboot = async () => {
    const confirmed = window.confirm(`Send a reboot command to ${device.serial}?`);
    if (!confirmed) return;

    setRebooting(true);
    setRebootFeedback(null);
    try {
      await axios.post(
        `${API_BASE_URL}/devices/${device.serial}/reboot`,
        {},
        { withCredentials: true }
      );
      setRebootFeedback({ type: 'success', text: 'Reboot command sent.' });
    } catch (error: any) {
      console.error('Failed to send reboot command:', error);
      setRebootFeedback({
        type: 'error',
        text: error.response?.data?.message || 'Failed to send reboot command.',
      });
    } finally {
      setRebooting(false);
    }
  };

  const handleSendCommand = async () => {
    const command = commandText.trim();
    if (!command || sendingCommand) return;

    setSendingCommand(true);
    setCommandFeedback(null);
    try {
      await axios.post(
        `${API_BASE_URL}/devices/${device.serial}/command`,
        { command },
        { withCredentials: true }
      );
      setCommandFeedback({ type: 'success', text: `Sent: "${command}"` });
      setCommandText('');
    } catch (error: any) {
      console.error('Failed to send command:', error);
      setCommandFeedback({
        type: 'error',
        text: error.response?.data?.message || 'Failed to send command.',
      });
    } finally {
      setSendingCommand(false);
    }
  };

  const handleDeleteDevice = async () => {
    if (!onDelete) return;

    const confirmed = window.confirm(`Delete device ${device.serial}? This cannot be undone.`);
    if (!confirmed) return;

    setDeleting(true);
    setDeleteError(null);

    try {
      await axios.delete(`${API_BASE_URL}/devices/${device.serial}`, {
        withCredentials: true,
      });
      onDelete(device);
      onClose();
    } catch (error: any) {
      console.error('Failed to delete device:', error);
      setDeleteError(error.response?.data?.message || 'Failed to delete device');
    } finally {
      setDeleting(false);
    }
  };

  const handleUnassign = async () => {
    setAssignLoading(true);
    setUserActionError(null);
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/devices/${device.serial}`,
        { user: null },
        { withCredentials: true }
      );

      const updatedDevice = normalizeDeviceFromResponse(device, response.data.device);
      setAssignedUser(null);
      notifyChange(updatedDevice);
    } catch (error: any) {
      console.error('Failed to unassign device:', error);
      setUserActionError(error.response?.data?.message || 'Failed to unassign device');
    } finally {
      setAssignLoading(false);
    }
  };

  const handleAssign = async (user: User) => {
    setUserActionError(null);
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/devices/${device.serial}`,
        { user: user._id },
        { withCredentials: true }
      );

      const updatedDevice = normalizeDeviceFromResponse(device, {
        ...response.data.device,
        user: {
          _id: user._id,
          firstName: user.firstName,
          lastName: user.lastName,
        },
      });
      setAssignedUser(updatedDevice.user ?? null);
      setShowAssignModal(false);
      notifyChange(updatedDevice);
    } catch (error: any) {
      console.error('Failed to assign device:', error);
      setUserActionError(error.response?.data?.message || 'Failed to assign device');
      throw error;
    }
  };

  return (
    <>
      <div
        className={isPage ? '' : 'modal show d-block'}
        tabIndex={isPage ? undefined : -1}
        style={isPage ? undefined : { backgroundColor: 'rgba(0,0,0,0.5)' }}
      >
        <div className={isPage ? '' : 'modal-dialog modal-xl modal-dialog-scrollable'}>
          <div className={isPage ? '' : 'modal-content'}>
            <div className={isPage ? 'd-flex align-items-center justify-content-between mb-3' : 'modal-header'}>
              <h3 className={isPage ? 'mb-0' : 'modal-title'}>Edit Device</h3>
              {!isPage && (
                <button
                  type="button"
                  className="btn-close"
                  onClick={onClose}
                  aria-label="Close"
                />
              )}
            </div>

            <div className={isPage ? '' : 'modal-body'}>
              <div className="row g-3">
                {/* User */}
                <div className="col-12 col-lg-4">
                  <SectionTitle icon={faUser}>User</SectionTitle>
                  <div className="row g-2">
                    <div className="col-12">
                      <FieldTile label="Assignment">
                        <div className="small text-break">
                          {assignedUser ? (
                            <>
                              <Link to={`/users/${assignedUser._id}`}>
                                {getUserDisplayName(assignedUser)}
                              </Link>
                              {assignedUser.email && (
                                <div className="text-muted">{assignedUser.email}</div>
                              )}
                            </>
                          ) : (
                            <span className="text-muted">Unassigned</span>
                          )}
                        </div>
                      </FieldTile>
                    </div>
                    <div className="col-12">
                      <div className="border-bottom pb-2">
                        <div className="d-flex flex-wrap gap-2">
                          {assignedUser && (
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm"
                              onClick={handleUnassign}
                              disabled={assignLoading}
                            >
                              {assignLoading ? (
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                              ) : (
                                <>
                                  <FontAwesomeIcon icon={faUserMinus} className="me-1" />
                                  Unassign User
                                </>
                              )}
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => {
                              setUserActionError(null);
                              setShowAssignModal(true);
                            }}
                          >
                            <FontAwesomeIcon icon={faUserPlus} className="me-1" />
                            Assign User
                          </button>
                        </div>
                      </div>
                    </div>

                    {userActionError && (
                      <div className="col-12">
                        <div className="alert alert-danger py-2 mb-0">{userActionError}</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Deployment */}
                <div className="col-12 col-lg-8">
                  <SectionTitle icon={faRocket}>Deployment</SectionTitle>
                  <div className="row g-2">
                    <div className="col-12 col-sm-6 col-xl-4">
                      <FieldTile label="Type">
                        <select
                          className={`form-select form-select-sm fw-semibold ${typeControlClasses[type]}`}
                          value={type}
                          onChange={(e) => setType(e.target.value as Device['deployment']['type'])}
                        >
                          <option value="prod">Production (prod)</option>
                          <option value="beta">Beta Testing (beta)</option>
                          <option value="dev">Development (dev)</option>
                          <option value="deco">Decommissioned (deco)</option>
                        </select>
                      </FieldTile>
                    </div>
                    <div className="col-12 col-sm-6 col-xl-4">
                      <FieldTile label="Group">
                        <select
                          className="form-select form-select-sm"
                          value={group}
                          onChange={(e) => setGroup(e.target.value)}
                          disabled={!canEditDeploymentGroup}
                        >
                          <option value="">Select a group</option>
                          {alphabet.map((letter) => (
                            <option key={letter} value={letter}>
                              {letter}
                            </option>
                          ))}
                        </select>
                      </FieldTile>
                    </div>
                    <div className="w-100" />
                    <div className="col-12 col-sm-6 col-xl-4">
                      <FieldTile label="OTA">
                        <OtaModeSelect
                          id="edit-device-ota-mode"
                          value={otaMode}
                          onChange={setOtaMode}
                          size="sm"
                        />
                      </FieldTile>
                    </div>
                    <div className="col-12 col-sm-6 col-xl-4">
                      <ReadOnlyField label="Last Update Method" value={device.deployment?.lastUpdateMethod} />
                    </div>
                    <div className="col-12 col-sm-6 col-xl-4">
                      <ReadOnlyField
                        label="Last Update Time"
                        value={formatDateTime(device.deployment?.lastUpdateTime)}
                        italic
                      />
                    </div>
                  </div>
                </div>

                {/* Device */}
                <div className="col-12">
                  <div className="border-top pt-3">
                    <SectionTitle icon={faMicrochip}>Device</SectionTitle>
                  </div>
                </div>
                <div className="col-12 col-sm-6 col-xl-3">
                  <ReadOnlyField label="Serial" value={device.serial} />
                </div>
                <div className="col-12 col-sm-6 col-xl-3">
                  <ReadOnlyField label="Model" value={device.model} />
                </div>
                <div className="col-12 col-sm-6 col-xl-3">
                  <ReadOnlyField label="Firmware" value={device.firmware} />
                </div>
                <div className="col-12 col-sm-6 col-xl-3">
                  <ReadOnlyField label="Record Created" value={formatDateTime(device.recordCreated)} italic />
                </div>
                <div className="col-12 col-sm-6 col-xl-3">
                  <FieldTile label="Logging Mode">
                    <select
                      className="form-select form-select-sm"
                      value={loggingMode}
                      onChange={(e) => void handleLoggingModeChange(Number(e.target.value))}
                      disabled={updatingLoggingMode}
                      style={{ width: '150px' }}
                    >
                      {LOGGING_MODE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {updatingLoggingMode && (
                      <div className="small text-muted mt-1">Sending to device…</div>
                    )}
                    {loggingModeError && (
                      <div className="small text-danger mt-1">{loggingModeError}</div>
                    )}
                  </FieldTile>
                </div>
                <div className="col-12 col-sm-6 col-xl-3">
                  <FieldTile label="Reboot">
                    <span
                      title={supportsRemoteCommands ? undefined : COMMAND_UNSUPPORTED_TOOLTIP}
                      style={{ display: 'inline-block' }}
                    >
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={handleReboot}
                        disabled={rebooting || !supportsRemoteCommands}
                      >
                        {rebooting ? (
                          <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                        ) : (
                          <FontAwesomeIcon icon={faPowerOff} className="me-1" />
                        )}
                        Reboot
                      </button>
                    </span>
                    {rebootFeedback && (
                      <div className={`small mt-1 ${rebootFeedback.type === 'success' ? 'text-success' : 'text-danger'}`}>
                        {rebootFeedback.text}
                      </div>
                    )}
                  </FieldTile>
                </div>

                {/* Hardware */}
                <div className="col-12">
                  <div className="border-top pt-3 d-flex align-items-center justify-content-between gap-2">
                    <SectionTitle icon={faMemory}>Hardware</SectionTitle>
                    <span
                      title={canEditHardwareProfile ? undefined : HARDWARE_PROFILE_TOOLTIP}
                      style={{ display: 'inline-block' }}
                    >
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        disabled={!canEditHardwareProfile}
                        onClick={() => setShowHardwareProfileModal(true)}
                      >
                        <FontAwesomeIcon icon={faPen} className="me-1" />
                        Edit
                      </button>
                    </span>
                  </div>
                </div>
                <div className="col-12 col-sm-6 col-xl-3">
                  <ReadOnlyField label="MAC Address" value={device.hardware?.macAddress} />
                </div>
                <div className="col-12 col-sm-6 col-xl-3">
                  <ReadOnlyField
                    label="PCB Type"
                    value={labelForOption(PCB_TYPE_OPTIONS, device.hardware?.pcbType)}
                  />
                </div>
                <div className="col-12 col-sm-6 col-xl-3">
                  <ReadOnlyField
                    label="PCB Revision"
                    value={device.hardware?.pcbRevision ? `Rev ${device.hardware.pcbRevision}` : device.hardware?.pcbRevision}
                  />
                </div>
                <div className="col-12 col-sm-6 col-xl-3">
                  <ReadOnlyField
                    label="Model Type"
                    value={labelForOption(MODEL_TYPE_OPTIONS, device.hardware?.modelType)}
                  />
                </div>

                {/* Stats */}
                <div className="col-12">
                  <div className="border-top pt-3">
                    <SectionTitle icon={faChartSimple}>Stats</SectionTitle>
                  </div>
                </div>
                <div className="col-12 col-sm-6 col-xl-3">
                  <ReadOnlyField
                    label="Last Check-In Time"
                    value={formatDateTime(device.stats?.lastCheckInTime)}
                    italic
                  />
                </div>
                <div className="col-12 col-sm-6 col-xl-3">
                  <ReadOnlyField
                    label="Last App Ping"
                    value={formatDateTime(device.stats?.lastAppPing)}
                    italic
                  />
                </div>
                <div className="col-12 col-sm-6 col-xl-3">
                  <FieldTile label="MQTT Online">
                    {renderMqttOnlineIndicator()}
                  </FieldTile>
                </div>
                <div className="col-12 col-sm-6 col-xl-3">
                  <ReadOnlyField label="MQTT Last Seen" value={formatDateTime(device.mqttLastSeenAt)} italic />
                </div>
                <div className="col-12 col-sm-6 col-xl-3">
                  <ReadOnlyField label="MQTT Disconnect Reason" value={device.mqttDisconnectReason} />
                </div>

                {/* Factory */}
                <div className="col-12">
                  <div className="border-top pt-3">
                    <SectionTitle icon={faIndustry}>Factory</SectionTitle>
                  </div>
                </div>
                <div className="col-12 col-sm-6 col-xl-3">
                  <ReadOnlyField label="Flashed" value={formatDateTime(device.factory?.flashed)} italic />
                </div>
                <div className="col-12 col-md-6 col-xl-3">
                  <ReadOnlyField label="Firmware" value={device.factory?.firmware} />
                </div>
                <div className="col-12 col-md-6 col-xl-3">
                  <ReadOnlyField label="Bootloader" value={device.factory?.bootloader} />
                </div>
                <div className="col-12 col-md-6 col-xl-3">
                  <ReadOnlyField label="Partition Table" value={device.factory?.partitionTable} />
                </div>
                <div className="col-12 col-md-6 col-xl-3">
                  <ReadOnlyField label="ESP32 Chip Revision" value={device.factory?.esp32ChipRevision} />
                </div>

                {/* Command */}
                <div className="col-12">
                  <div className="border-top pt-3">
                    <SectionTitle icon={faTerminal}>Command</SectionTitle>
                  </div>
                </div>
                <div className="col-12">
                  <div
                    className="input-group input-group-sm"
                    title={supportsRemoteCommands ? undefined : COMMAND_UNSUPPORTED_TOOLTIP}
                  >
                    <input
                      type="text"
                      className="form-control font-monospace"
                      placeholder="Type a command to send over MQTT (e.g. reboot)"
                      value={commandText}
                      onChange={(e) => setCommandText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleSendCommand();
                        }
                      }}
                      disabled={sendingCommand || !supportsRemoteCommands}
                    />
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleSendCommand}
                      disabled={sendingCommand || !supportsRemoteCommands || !commandText.trim()}
                    >
                      {sendingCommand ? (
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                      ) : (
                        <FontAwesomeIcon icon={faPaperPlane} className="me-1" />
                      )}
                      Enter
                    </button>
                  </div>
                  {!supportsRemoteCommands && (
                    <div className="small mt-1 text-muted">
                      This device's firmware ({device.firmware || 'unknown'}) does not support receiving commands over MQTT. Requires v{MIN_COMMAND_FIRMWARE}+.
                    </div>
                  )}
                  {commandFeedback && (
                    <div className={`small mt-1 ${commandFeedback.type === 'success' ? 'text-success' : 'text-danger'}`}>
                      {commandFeedback.text}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className={`${isPage ? 'border-top pt-3 mt-3' : 'modal-footer'} d-flex justify-content-between`}>
              <div>
                {onDelete && (
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={handleDeleteDevice}
                    disabled={deleting || saving}
                  >
                    {deleting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faTrash} className="me-2" />
                        Delete Device
                      </>
                    )}
                  </button>
                )}
              </div>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onClose}
                  disabled={deleting}
                >
                  <FontAwesomeIcon icon={faTimes} className="me-2" />
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={saving || deleting}
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faSave} className="me-2" />
                      Save
                    </>
                  )}
                </button>
              </div>
            </div>
            {deleteError && (
              <div className="px-3 pb-3">
                <div className="alert alert-danger py-2 mb-0">{deleteError}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      <AssignUserModal
        isOpen={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        onAssign={handleAssign}
        apiBaseUrl={API_BASE_URL}
      />

      <HardwareProfileModal
        apiBaseUrl={API_BASE_URL}
        device={device}
        isOpen={showHardwareProfileModal}
        onClose={() => setShowHardwareProfileModal(false)}
        onUpdated={(updatedDevice) => {
          notifyChange(normalizeDeviceFromResponse(device, updatedDevice));
        }}
      />
    </>
  );
};

export default EditDevice;
